import React, { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Trash2, X, LayoutDashboard } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { processService } from "../../services/process.service";
import { sindicatoService } from "../../services/sindicato.service";

function ViewCard({ view, onDelete }) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!window.confirm(`Excluir o painel "${view.nome}"?`)) return;
    setDeleting(true);
    try {
      await sindicatoService.deleteView(view.id);
      onDelete(view.id);
      toast.success("Painel excluído.");
    } catch {
      toast.error("Erro ao excluir o painel.");
      setDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm flex flex-col gap-3">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <LayoutDashboard className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
          <span className="font-semibold text-slate-800">{view.nome}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-slate-400 hover:text-red-500 shrink-0"
          onClick={handleDelete}
          disabled={deleting}
        >
          {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {view.processos.map((p) => (
          <span
            key={p.id}
            className="bg-slate-100 text-slate-600 text-xs px-2 py-0.5 rounded-full"
          >
            {p.nome_processo}
          </span>
        ))}
      </div>
    </div>
  );
}

function NewViewForm({ processos, onSave, onCancel }) {
  const [nome, setNome] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleId = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleSave = async () => {
    if (!nome.trim()) {
      toast.error("Informe um nome para o painel.");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("Selecione ao menos um processo.");
      return;
    }
    setSaving(true);
    try {
      const created = await sindicatoService.createView({ nome: nome.trim(), processo_ids: selectedIds });
      onSave(created);
      toast.success("Painel criado com sucesso.");
    } catch {
      toast.error("Erro ao criar o painel.");
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-indigo-200 p-5 shadow-sm space-y-4">
      <div className="flex items-center justify-between">
        <p className="font-semibold text-slate-800">Novo Painel</p>
        <button onClick={onCancel} className="text-slate-400 hover:text-slate-600">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-1">Nome do Painel</label>
        <input
          type="text"
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ex: Relatório Mensal"
          className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-300"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-500 mb-2">Processos</label>
        <div className="space-y-2 max-h-52 overflow-y-auto pr-1">
          {processos.map((p) => (
            <label
              key={p.id}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(p.id)}
                onChange={() => toggleId(p.id)}
                className="accent-indigo-600 w-4 h-4 shrink-0"
              />
              <span className="text-sm text-slate-700 group-hover:text-slate-900">
                {p.nome_processo}
                {p.cnj && <span className="text-slate-400 ml-1.5 text-xs">{p.cnj}</span>}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="flex gap-2 justify-end pt-1">
        <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>
          Cancelar
        </Button>
        <Button size="sm" onClick={handleSave} disabled={saving}>
          {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
          Salvar
        </Button>
      </div>
    </div>
  );
}

export function SindicatoViewsManager() {
  const [views, setViews] = useState([]);
  const [processos, setProcessos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [v, p] = await Promise.all([
        sindicatoService.getViews(),
        processService.getProcessos(),
      ]);
      setViews(v);
      setProcessos(p);
    } catch {
      toast.error("Erro ao carregar painéis do sindicato.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSaved = (newView) => {
    setViews((prev) => [newView, ...prev]);
    setShowForm(false);
  };

  const handleDeleted = (id) => {
    setViews((prev) => prev.filter((v) => v.id !== id));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24 text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Carregando...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800">Painéis do Sindicato</h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Cada painel agrega os resultados dos processos selecionados.
          </p>
        </div>
        {!showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            Novo Painel
          </Button>
        )}
      </div>

      {showForm && (
        <NewViewForm
          processos={processos}
          onSave={handleSaved}
          onCancel={() => setShowForm(false)}
        />
      )}

      {views.length === 0 && !showForm ? (
        <div className="flex flex-col items-center py-16 text-slate-400 gap-2">
          <LayoutDashboard className="w-8 h-8 opacity-40" />
          <p className="text-sm">Nenhum painel cadastrado.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {views.map((v) => (
            <ViewCard key={v.id} view={v} onDelete={handleDeleted} />
          ))}
        </div>
      )}
    </div>
  );
}
